'use client';

import * as React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ExternalLink, Loader2, Rocket, TriangleAlert, XCircle } from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/api/trpc';

type PublishResult = inferRouterOutputs<AppRouter>['partners']['publishConfig'];

/**
 * One-click "publish to production" for a DB-branded partner.
 *
 * Commits the host→Clerk-satellite mapping into the crm-app PARTNERS registry
 * on `main` (the only thing Clerk needs in the bundle synchronously) and
 * appends the root domain to the backend CORS allowlist — so a non-dev can
 * onboard a partner end-to-end without touching the repo. The published entry
 * is `brandSource:'db'`; identity comes from the DB branding overlay.
 *
 * Gated behind a confirmation modal because it triggers a production deploy.
 */
export function PartnerPublish({
  brand,
  rootDomain,
  subdomain,
  label,
}: {
  brand: string;
  rootDomain: string;
  subdomain: string;
  label?: string;
}) {
  const { t } = useTranslation('partnerProvisioning');
  const publish = trpc.partners.publishConfig.useMutation();
  const [result, setResult] = React.useState<PublishResult | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const trimmedRoot = rootDomain.trim().toLowerCase();
  const trimmedSub = subdomain.trim().toLowerCase();
  const trimmedBrand = brand.trim().toLowerCase();
  const appHost = `${trimmedSub || '<sub>'}.${trimmedRoot || '<root>'}`;
  const canPublish =
    trimmedBrand.length >= 2 &&
    trimmedRoot.length >= 2 &&
    trimmedSub.length >= 1 &&
    !publish.isPending;

  const run = React.useCallback(async () => {
    try {
      const data = await publish.mutateAsync({
        brand: trimmedBrand,
        rootDomain: trimmedRoot,
        subdomain: trimmedSub,
        label: label?.trim() || undefined,
      });
      setResult(data);
      setConfirmOpen(false);
      if (data.publish.committed || data.cors.ok) {
        toast.success(t('toast.published'));
      } else {
        toast.error(data.publish.committed === false ? data.publish.reason : t('toast.publishFailed'));
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }, [publish, trimmedBrand, trimmedRoot, trimmedSub, label, t]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          disabled={!canPublish}
          onClick={() => setConfirmOpen(true)}
        >
          {publish.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Rocket className="mr-2 h-4 w-4" />
          )}
          {publish.isPending ? t('publish.publishing') : t('publish.button')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('publish.hint', { appHost })}</p>
      </div>

      {result ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
          {result.publish.committed ? (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              {t('publish.result.committed')}
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1">
              <TriangleAlert className="h-3.5 w-3.5 text-amber-500" />
              {result.publish.reason}
            </Badge>
          )}
          {result.publish.committed ? (
            <a
              href={result.publish.commitUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('publish.result.commitLink')}
            </a>
          ) : null}
          <Badge variant={result.cors.ok ? 'secondary' : 'outline'} className="gap-1">
            {result.cors.ok ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-destructive" />
            )}
            {result.cors.ok
              ? result.cors.changed
                ? t('publish.result.corsUpdated')
                : t('publish.result.corsUnchanged')
              : t('publish.result.corsFailed')}
          </Badge>
        </div>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-amber-500" />
              {t('publish.confirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('publish.confirm.description', { appHost })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('publish.confirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run()}>
              {t('publish.confirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
